// `@Name` in a chat message (PLAN-2026-10-27-more-fun-in-chat C2, scenario I1).
//
// ONE RULE FOR THE SERVER AND EVERY CLIENT: the store finds whom a message
// names among the league's members, whether the name was picked from the
// box's suggestions or typed out, and a client marks the same words in the
// message it draws. A name is `@` and a member's display name, any case,
// followed by the end or by something that is not a letter or a digit, so
// `@Dave's` names Dave and `@Davey` does not. The longest name wins where two
// begin alike: `@Ken W` is Ken W, not Ken.

export interface MentionPiece {
  text: string;
  /** The member's name as given, when this piece is `@` and their name. */
  name?: string;
}

const WORD = /[\p{L}\p{N}]/u;

/**
 * The message cut into plain words and mentions, in order. `names` are the
 * names that may be mentioned; an `@` before anything else stays as written.
 */
export function splitMentions(body: string, names: readonly string[]): MentionPiece[] {
  const byLength = [...new Set(names.filter((n) => n.trim() !== ''))].sort(
    (a, b) => b.length - a.length,
  );
  const out: MentionPiece[] = [];
  let plain = '';
  let i = 0;
  while (i < body.length) {
    if (body[i] === '@' && (i === 0 || !WORD.test(body[i - 1]!))) {
      const rest = body.slice(i + 1);
      const name = byLength.find((n) => {
        if (rest.slice(0, n.length).toLowerCase() !== n.toLowerCase()) return false;
        const after = rest[n.length];
        return after === undefined || !WORD.test(after);
      });
      if (name !== undefined) {
        if (plain !== '') out.push({ text: plain });
        plain = '';
        out.push({ text: body.slice(i, i + 1 + name.length), name });
        i += 1 + name.length;
        continue;
      }
    }
    plain += body[i];
    i += 1;
  }
  if (plain !== '') out.push({ text: plain });
  return out;
}

/** The names a message mentions, each once, in the order first named. */
export function mentionedIn(body: string, names: readonly string[]): string[] {
  const found: string[] = [];
  for (const p of splitMentions(body, names))
    if (p.name !== undefined && !found.includes(p.name)) found.push(p.name);
  return found;
}
